import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  LifeBuoy,
  User,
  ArrowLeft,
  Clock,
  AlarmClock,
  Search,
  Calendar,
  Filter,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  MessageSquare,
  Circle,
  RotateCcw,
  Eye,
  FolderIcon,
  Banknote,
  Gamepad2,
  Bug,
  HelpCircle,
  ImageIcon
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableRow, 
  TableHead, 
  TableCell 
} from '@/components/ui/table';

// Helper component for ticket status badges
function TicketStatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant={
        status === 'open' ? 'default' :
        status === 'in_progress' ? 'secondary' :
        'outline'
      }
    >
      {status.replace('_', ' ')}
    </Badge>
  );
}

// Helper component for ticket priority badges  
function TicketPriorityBadge({ priority }: { priority: string }) {
  return (
    <Badge
      variant={
        priority === 'high' ? 'destructive' :
        priority === 'medium' ? 'default' :
        'outline'
      }
    >
      {priority}
    </Badge>
  );
}

// Helper component for stat cards
function StatCard({ 
  title, 
  count, 
  color, 
  icon 
}: { 
  title: string, 
  count: number, 
  color: string,
  icon: React.ReactNode
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{count}</p>
          </div>
          <div className={color}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// Helper component for circular progress
function CircularProgress({ 
  value, 
  className, 
  strokeColor = "currentColor" 
}: { 
  value: number, 
  className?: string, 
  strokeColor?: string 
}) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  
  return (
    <svg className={className} viewBox="0 0 50 50">
      <circle
        className="stroke-current"
        style={{ color: strokeColor }}
        fill="none"
        strokeWidth="5"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        r={radius}
        cx="25"
        cy="25"
      />
    </svg>
  );
}

// Helper function to format dates
function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export default function AdminSupportPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { id } = useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Redirect if not admin
  if (!user || user.role !== "admin") {
    setLocation("/");
    return null;
  }

  // If there's a specific ticket ID, show the detail view
  if (id) {
    return <TicketDetailView ticketId={Number(id)} />;
  }

  // Otherwise show the tickets listing
  return <TicketsListView />;
}

function TicketsListView() {
  const [, setLocation] = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Quick update mutation
  const quickUpdateMutation = useMutation({
    mutationFn: async ({ id, updateData }: { id: number, updateData: any }) => {
      const res = await apiRequest(
        "PATCH", 
        `/api/admin/support-tickets/${id}`,
        updateData
      );
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      toast({
        title: "Ticket updated",
        description: "The ticket has been updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating ticket",
        description: error.message || "An error occurred while updating the ticket",
        variant: "destructive",
      });
    },
  });
  
  // Handle quick status/priority updates
  const handleQuickUpdate = (id: number, updateData: any) => {
    quickUpdateMutation.mutate({ id, updateData });
  };

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["admin-support-tickets"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/support-tickets");
      return await res.json();
    },
  });

  // Apply filters to tickets
  const filteredTickets = React.useMemo(() => {
    if (!tickets) return [];
    
    return tickets.filter((ticket: any) => {
      // Apply search filter
      const matchesSearch = 
        searchTerm === '' || 
        ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(ticket.userId).includes(searchTerm);
      
      // Apply status filter
      const matchesStatus = 
        statusFilter === 'all' || 
        ticket.status === statusFilter;
      
      // Apply priority filter
      const matchesPriority = 
        priorityFilter === 'all' || 
        ticket.priority === priorityFilter;
      
      // Apply feedback filter
      const matchesFeedback =
        feedbackFilter === 'all' || 
        (feedbackFilter === 'positive' && ticket.feedbackType === 'positive') ||
        (feedbackFilter === 'negative' && ticket.feedbackType === 'negative') ||
        (feedbackFilter === 'pending' && ticket.adminResponse && !ticket.feedbackType) ||
        (feedbackFilter === 'none' && !ticket.adminResponse);
      
      return matchesSearch && matchesStatus && matchesPriority && matchesFeedback;
    });
  }, [tickets, searchTerm, statusFilter, priorityFilter, feedbackFilter]);
  
  // Calculate feedback statistics
  const feedbackStats = React.useMemo(() => {
    if (!tickets) return { positive: 0, negative: 0, pending: 0, none: 0, total: 0, positivePercentage: 0 };
    
    const stats = {
      positive: tickets.filter((t: any) => t.feedbackType === 'positive').length,
      negative: tickets.filter((t: any) => t.feedbackType === 'negative').length,
      pending: tickets.filter((t: any) => t.adminResponse && !t.feedbackType).length,
      none: tickets.filter((t: any) => !t.adminResponse).length,
    };
    
    const totalWithFeedback = stats.positive + stats.negative;
    const positivePercentage = totalWithFeedback > 0 
      ? Math.round((stats.positive / totalWithFeedback) * 100) 
      : 0;
      
    return {
      ...stats,
      total: tickets.length,
      positivePercentage
    };
  }, [tickets]);

  const handleBackToAdmin = () => {
    setLocation('/admin');
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-8 animate-pulse">
        <div className="h-10 bg-muted/50 rounded w-1/4 mb-8"></div>
        <div className="h-40 bg-muted/50 rounded mb-8"></div>
        <div className="h-96 bg-muted/50 rounded"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 pb-20">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleBackToAdmin}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <LifeBuoy className="h-8 w-8" />
            Support Ticket Management
          </h1>
        </div>
      </div>
      
      {/* Ticket Status Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard 
          title="Open Tickets" 
          count={tickets?.filter((t: any) => t.status === "open").length || 0}
          color="text-yellow-500"
          icon={<AlertCircle className="h-5 w-5" />}
        />
        <StatCard 
          title="In Progress" 
          count={tickets?.filter((t: any) => t.status === "in_progress").length || 0}
          color="text-blue-500"
          icon={<Clock className="h-5 w-5" />}
        />
        <StatCard 
          title="Closed Tickets" 
          count={tickets?.filter((t: any) => t.status === "closed").length || 0} 
          color="text-green-500"
          icon={<CheckCircle className="h-5 w-5" />}
        />
        <StatCard 
          title="High Priority" 
          count={tickets?.filter((t: any) => t.priority === "high").length || 0}
          color="text-red-500" 
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      </div>
      
      {/* Feedback Stats */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Feedback Statistics</CardTitle>
          <CardDescription>
            Overview of user feedback on support ticket responses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Positive Feedback */}
            <div className="bg-card border rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Positive Feedback</p>
                  <h3 className="text-2xl font-bold text-green-500">{feedbackStats.positive}</h3>
                </div>
                <div className="bg-green-100 dark:bg-green-900/30 text-green-500 p-2 rounded-full">
                  <ThumbsUp className="h-6 w-6" />
                </div>
              </div>
            </div>
            
            {/* Negative Feedback */}
            <div className="bg-card border rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Negative Feedback</p>
                  <h3 className="text-2xl font-bold text-red-500">{feedbackStats.negative}</h3>
                </div>
                <div className="bg-red-100 dark:bg-red-900/30 text-red-500 p-2 rounded-full">
                  <ThumbsDown className="h-6 w-6" />
                </div>
              </div>
            </div>
            
            {/* Pending Feedback */}
            <div className="bg-card border rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Feedback</p>
                  <h3 className="text-2xl font-bold text-amber-500">{feedbackStats.pending}</h3>
                </div>
                <div className="bg-amber-100 dark:bg-amber-900/30 text-amber-500 p-2 rounded-full">
                  <Clock className="h-6 w-6" />
                </div>
              </div>
            </div>
            
            {/* No Response */}
            <div className="bg-card border rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">No Response</p>
                  <h3 className="text-2xl font-bold text-muted-foreground">{feedbackStats.none}</h3>
                </div>
                <div className="bg-gray-100 dark:bg-gray-800 text-muted-foreground p-2 rounded-full">
                  <MessageSquare className="h-6 w-6" />
                </div>
              </div>
            </div>
            
            {/* Satisfaction Rate */}
            <div className="bg-card border rounded-lg shadow-sm p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Satisfaction Rate</p>
                  <h3 className="text-2xl font-bold">
                    {feedbackStats.positivePercentage}%
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {feedbackStats.positive + feedbackStats.negative === 0 
                      ? "No feedback yet" 
                      : `Based on ${feedbackStats.positive + feedbackStats.negative} responses`}
                  </p>
                </div>
                <div className="relative">
                  <Circle className="h-12 w-12 text-muted-foreground/20" />
                  <CircularProgress 
                    value={feedbackStats.positivePercentage} 
                    className="h-12 w-12 absolute top-0 left-0" 
                    strokeColor="var(--green-500)"
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Filters */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Filter Support Tickets</CardTitle>
          <CardDescription>
            Use the filters below to find specific tickets.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by subject or description"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Select
                value={statusFilter}
                onValueChange={setStatusFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <AlarmClock className="h-4 w-4 text-muted-foreground" />
              <Select
                value={priorityFilter}
                onValueChange={setPriorityFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select
                value={feedbackFilter}
                onValueChange={setFeedbackFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by feedback" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Feedback</SelectItem>
                  <SelectItem value="positive">
                    <div className="flex items-center gap-1">
                      <ThumbsUp className="h-3 w-3 text-green-500" />
                      <span>Positive</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="negative">
                    <div className="flex items-center gap-1">
                      <ThumbsDown className="h-3 w-3 text-red-500" />
                      <span>Negative</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="pending">Pending Feedback</SelectItem>
                  <SelectItem value="none">No Response Yet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tickets List */}
      <Card>
        <CardHeader>
          <CardTitle>Support Tickets</CardTitle>
          <CardDescription>
            Total tickets: {filteredTickets.length} 
            {filteredTickets.length !== tickets?.length && ` (filtered from ${tickets?.length} total)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTickets.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No support tickets found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Feedback</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTickets.map((ticket: any) => (
                  <TableRow key={ticket.id} >
                    <TableCell>{ticket.id}</TableCell>
                    <TableCell>{ticket.userId}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {ticket.subject}
                    </TableCell>
                    <TableCell>
                      <TicketStatusBadge status={ticket.status} />
                    </TableCell>
                    <TableCell>
                      <TicketPriorityBadge priority={ticket.priority} />
                    </TableCell>
                    <TableCell>
                      {formatDate(new Date(ticket.createdAt))}
                    </TableCell>
                    <TableCell>
                      {ticket.feedbackType ? (
                        <Badge 
                          variant={ticket.feedbackType === 'positive' ? 'success' : 'destructive'}
                          className="flex items-center gap-1"
                        >
                          {ticket.feedbackType === 'positive' ? (
                            <>
                              <ThumbsUp className="h-3 w-3" />
                              Positive
                            </>
                          ) : (
                            <>
                              <ThumbsDown className="h-3 w-3" />
                              Negative
                            </>
                          )}
                        </Badge>
                      ) : ticket.adminResponse ? (
                        <span className="text-xs text-muted-foreground">Pending</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setLocation(`/admin/support/${ticket.id}`)}
                          title="View details"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant={ticket.status === 'open' ? 'outline' : 'ghost'}
                          size="icon"
                          onClick={() => {
                            const newStatus = ticket.status === 'open' ? 'in_progress' : 'open';
                            handleQuickUpdate(ticket.id, { status: newStatus });
                          }}
                          title={ticket.status === 'open' ? 'Mark as in progress' : 'Mark as open'}
                        >
                          {ticket.status === 'open' ? 
                            <Clock className="h-4 w-4 text-amber-500" /> : 
                            <AlertCircle className="h-4 w-4 text-blue-500" />
                          }
                        </Button>
                        <Button 
                          variant={ticket.status === 'closed' ? 'outline' : 'ghost'}
                          size="icon"
                          onClick={() => {
                            const newStatus = ticket.status === 'closed' ? 'in_progress' : 'closed';
                            handleQuickUpdate(ticket.id, { status: newStatus });
                          }}
                          title={ticket.status === 'closed' ? 'Reopen ticket' : 'Close ticket'}
                        >
                          {ticket.status === 'closed' ? 
                            <RotateCcw className="h-4 w-4 text-red-500" /> : 
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          }
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TicketDetailView({ ticketId }: { ticketId: number }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  const { data: ticket, isLoading } = useQuery({
    queryKey: ["admin-support-ticket", ticketId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/support-tickets/${ticketId}`);
      return await res.json();
    },
  });
  
  const [statusValue, setStatusValue] = useState<string>('');
  const [priorityValue, setPriorityValue] = useState<string>('');
  const [adminResponse, setAdminResponse] = useState<string>('');
  
  // Set form values once ticket data is loaded
  React.useEffect(() => {
    if (ticket) {
      setStatusValue(ticket.status);
      setPriorityValue(ticket.priority);
      setAdminResponse(ticket.adminResponse || '');
    }
  }, [ticket]);

  // Get category icon based on category name
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'profile':
        return <User className="h-4 w-4" />;
      case 'account':
        return <FolderIcon className="h-4 w-4" />;
      case 'coin_purchase':
        return <Banknote className="h-4 w-4" />;
      case 'gameplay':
        return <Gamepad2 className="h-4 w-4" />;
      case 'bugs':
        return <Bug className="h-4 w-4" />;
      case 'other':
      default:
        return <HelpCircle className="h-4 w-4" />;
    }
  };

  // Get category label
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'profile':
        return 'Profile';
      case 'account':
        return 'Account';
      case 'coin_purchase':
        return 'Coin Purchase';
      case 'gameplay':
        return 'Gameplay';
      case 'bugs':
        return 'Bugs';
      case 'other':
      default:
        return 'Other';
    }
  };

  // Category badge
  const getCategoryBadge = (category: string) => {
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {getCategoryIcon(category)}
        {getCategoryLabel(category)}
      </Badge>
    );
  };
  
  const mutation = useMutation({
    mutationFn: async (updateData: any) => {
      const res = await apiRequest(
        "PATCH", 
        `/api/admin/support-tickets/${ticketId}`,
        updateData
      );
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-support-ticket", ticketId] });
      queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      toast({
        title: "Ticket updated",
        description: "The support ticket has been updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating ticket",
        description: error.message || "An error occurred while updating the ticket",
        variant: "destructive",
      });
    },
  });
  
  const handleStatusChange = (value: string) => {
    setStatusValue(value);
  };
  
  const handlePriorityChange = (value: string) => {
    setPriorityValue(value);
  };
  
  const handleSubmit = () => {
    const updateData: any = {};
    
    // Only include fields that have changed
    if (statusValue !== ticket?.status) {
      updateData.status = statusValue;
    }
    
    if (priorityValue !== ticket?.priority) {
      updateData.priority = priorityValue;
    }
    
    if (adminResponse !== ticket?.adminResponse) {
      updateData.adminResponse = adminResponse;
    }
    
    // Only submit if there are changes
    if (Object.keys(updateData).length > 0) {
      mutation.mutate(updateData);
    } else {
      toast({
        title: "No changes made",
        description: "Make some changes before updating the ticket",
      });
    }
  };
  
  const handleBack = () => {
    setLocation('/admin/support');
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto p-8 animate-pulse">
        <div className="h-10 bg-muted/50 rounded w-1/4 mb-8"></div>
        <div className="h-40 bg-muted/50 rounded mb-8"></div>
        <div className="h-96 bg-muted/50 rounded"></div>
      </div>
    );
  }
  
  if (!ticket) {
    return (
      <div className="container mx-auto p-8">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="outline" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Support Ticket Not Found</h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <p>The requested ticket could not be found.</p>
            <Button onClick={handleBack} className="mt-4">
              Back to Support Tickets
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-8 pb-20">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="outline" size="icon" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <LifeBuoy className="h-6 w-6" />
          Ticket #{ticket.id}
        </h1>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          {/* Ticket subject and description card */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{ticket.subject}</CardTitle>
                  <CardDescription className="flex flex-wrap items-center gap-4 mt-1">
                    <span className="flex items-center gap-1">
                      <User className="h-4 w-4" /> User ID: {ticket.userId}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" /> 
                      Created: {formatDate(new Date(ticket.createdAt))}
                    </span>
                    {ticket.category && (
                      <span>
                        {getCategoryBadge(ticket.category)}
                      </span>
                    )}
                    {ticket.imageUrls && ticket.imageUrls.length > 0 && (
                      <span className="flex items-center gap-1">
                        <ImageIcon className="h-4 w-4 text-blue-500" />
                        <span className="text-blue-500 font-medium">{ticket.imageUrls.length} {ticket.imageUrls.length === 1 ? 'image' : 'images'} attached</span>
                      </span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <TicketStatusBadge status={ticket.status} />
                  <TicketPriorityBadge priority={ticket.priority} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Display attached images at the top if any */}
                {ticket.imageUrls && ticket.imageUrls.length > 0 ? (
                  <div className="mb-6 bg-muted/20 p-4 rounded-lg border border-blue-200 dark:border-blue-900">
                    <div className="font-medium text-sm mb-3 flex items-center gap-2">
                      <ImageIcon className="h-5 w-5 text-blue-500" />
                      <span className="text-blue-800 dark:text-blue-400">Attached Images ({ticket.imageUrls.length}):</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {ticket.imageUrls.map((imageUrl: string, index: number) => (
                        <div 
                          key={index}
                          className="relative cursor-pointer border rounded-md overflow-hidden hover:opacity-90 shadow-md"
                          onClick={() => handleImageClick(imageUrl)}
                        >
                          <img
                            src={imageUrl}
                            alt={`Image ${index + 1}`}
                            className="w-32 h-32 object-cover"
                            onError={(e) => {
                              console.error(`Image failed to load: ${imageUrl}`);
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = '/placeholder-image.jpg';
                              target.style.opacity = '0.4';
                              target.parentElement?.classList.add('bg-red-50');
                              const errorDiv = document.createElement('div');
                              errorDiv.innerText = `Error loading image`;
                              errorDiv.className = 'absolute inset-0 flex items-center justify-center text-xs text-red-500 bg-white/80 p-1 text-center';
                              target.parentElement?.appendChild(errorDiv);
                            }}
                          />
                          <div className="absolute inset-0 bg-black/0 hover:bg-black/20 flex items-center justify-center transition-colors">
                            <Eye className="h-6 w-6 text-white opacity-0 hover:opacity-100" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-0.5 px-2 text-center">
                            Click to view
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Debug output of image URLs */}
                    <details className="mt-3 text-xs text-muted-foreground">
                      <summary className="cursor-pointer hover:text-blue-500">Debug Image URLs</summary>
                      <pre className="mt-1 p-2 bg-muted overflow-auto max-h-[200px] rounded text-[10px] whitespace-pre-wrap">
                        {JSON.stringify(ticket.imageUrls, null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : (
                  <div className="bg-muted/10 rounded-lg p-4 text-center text-sm text-muted-foreground mb-4">
                    No images attached to this ticket
                  </div>
                )}

                <div className="font-medium text-sm">User's description:</div>
                <div className="p-4 bg-muted/30 rounded-md whitespace-pre-wrap">
                  {ticket.description}
                </div>

                {/* Image preview dialog */}
                {selectedImage && (
                  <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
                    <div className="max-w-3xl max-h-full">
                      <img
                        src={selectedImage}
                        alt="Full size"
                        className="max-w-full max-h-full object-contain"
                      />
                      <Button 
                        className="absolute top-4 right-4" 
                        variant="destructive" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(null);
                        }}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Admin response card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Admin Response</CardTitle>
              <CardDescription>
                Provide a helpful response to the user's issue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Enter your response to the user..."
                className="min-h-[200px]  border border-input"
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
              />
            </CardContent>
            <CardFooter className="flex justify-end space-x-2">
              <Button
                disabled={mutation.isPending}
                onClick={handleSubmit}
              >
                {mutation.isPending ? "Updating..." : "Update Ticket"}
              </Button>
            </CardFooter>
          </Card>
          
          {/* User feedback card */}
          {ticket.feedbackType && (
            <Card className={
              ticket.feedbackType === 'positive' ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : 
              ticket.feedbackType === 'negative' ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : ''
            }>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  {ticket.feedbackType === 'positive' ? (
                    <>
                      <ThumbsUp className="h-5 w-5 text-green-500" />
                      <span className="text-green-600 dark:text-green-400">Positive Feedback</span>
                    </>
                  ) : (
                    <>
                      <ThumbsDown className="h-5 w-5 text-red-500" />
                      <span className="text-red-600 dark:text-red-400">Negative Feedback</span>
                    </>
                  )}
                </CardTitle>
                <CardDescription>
                  User provided feedback on {formatDate(new Date(ticket.updatedAt))}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {ticket.feedbackContent ? (
                  <div className="p-3 bg-white dark:bg-black/20 rounded-md border border-border">
                    <p className="whitespace-pre-wrap">{ticket.feedbackContent}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">User did not provide additional comments</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Ticket information sidebar */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Ticket Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={statusValue} onValueChange={handleStatusChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Priority</label>
                  <Select value={priorityValue} onValueChange={handlePriorityChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <div className="p-2 border rounded-md flex items-center">
                    {getCategoryIcon(ticket.category || 'other')}
                    <span className="ml-2">{getCategoryLabel(ticket.category || 'other')}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Updated</label>
                  <div className="p-2 border rounded-md">
                    {ticket.updatedAt ? formatDate(new Date(ticket.updatedAt)) : 'Never updated'}
                  </div>
                </div>
                
                {ticket.closedAt && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Closed</label>
                    <div className="p-2 border rounded-md">
                      {formatDate(new Date(ticket.closedAt))}
                    </div>
                  </div>
                )}

                {/* Show image attachment count */}
                {ticket.imageUrls && ticket.imageUrls.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Images</label>
                    <div className="p-2 border rounded-md flex items-center">
                      <ImageIcon className="h-4 w-4 mr-2" />
                      {ticket.imageUrls.length} {ticket.imageUrls.length === 1 ? 'image' : 'images'} attached
                    </div>
                  </div>
                )}
                
                {/* User Feedback summary */}
                {ticket.feedbackType && (
                  <div className="space-y-2 mt-6 pt-4 border-t">
                    <label className="text-sm font-medium flex items-center gap-2">
                      User Feedback
                      <Badge variant={ticket.feedbackType === 'positive' ? 'success' : 'destructive'}>
                        {ticket.feedbackType === 'positive' ? 'Positive' : 'Negative'}
                      </Badge>
                    </label>
                    {ticket.feedbackContent ? (
                      <div className="p-3 bg-muted/30 rounded-md whitespace-pre-wrap text-sm">
                        {ticket.feedbackContent.length > 100 
                          ? `${ticket.feedbackContent.substring(0, 100)}...` 
                          : ticket.feedbackContent}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground italic">
                        No detailed feedback provided
                      </div>
                    )}
                  </div>
                )}
                
                <Button 
                  className="w-full mt-4" 
                  disabled={mutation.isPending}
                  onClick={handleSubmit}
                >
                  {mutation.isPending ? "Updating..." : "Update Ticket"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}